<template lang="pug">
    .play-box
        template
            template(v-if='!isLoading')
                game-header(
                    :time='getTime'
                    :slug='type'
                    :game-type='parameters && parameters.courseType'
                    @soundStateChanged="headerKey = !headerKey",
                    :key="headerKey",
                    :is-homework="!!exerciseId"
                )
                .playbox__container(v-if="gameParameters && !isLoading && preparedItems && !gameFinished")
                    chameleon-game-container(
                      :prepared-items="preparedItems"
                      :time="time"
                      :show-time="showTime"
                      :pictures="pictures"
                      :words-movement="wordsMovement"
                      :course-type="parameters && parameters.courseType"
                    )
                .revived-numbers-game-container__result-container(v-if='gameFinished')
                        fireworks
                        .play-box__reaction-container
                            result-image.play-box__image(status='success')
                        result-buttons.result_buttons(
                            :exercise-id="exerciseId"
                            :games-type="parameters && parameters.courseType"
                            :game-name="type"
                            :show-restart-button="showRestartButton"
                            @restartGame="restartGame"
                        )
                            slot
            cs-loader(v-else)
</template>

<script>
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import { mapState } from 'vuex';
    import _ from 'lodash';
    import trainerConfig from '../../../../config/trainer.json';
    import ChameleonGameContainer from './ChameleonGameContainer.vue';

    const OBJECT_OF_POSSIBLE_ANSWERS = {
        en: ['blue', 'orange', 'green', 'red', 'black', 'yellow', 'clap', 'jump'],
        ru: ['голубой', 'жёлтый', 'зелёный', 'красный', 'оранжевый', 'синий', 'чёрный', 'хлопок', 'прыжок'],
        uk: ['блакитний', 'жовтий', 'зелений', 'червоний', 'помаранчевий', 'синій', 'чорний', 'бавовна', 'стрибок'],
        lv: ['zils', 'dzeltens', 'zaļš', 'sarkans', 'oranžs', 'zils', 'melns', 'aplaudēt', 'lēciens'],
        fr: ['bleu', 'jaune', 'vert', 'rouge', 'orange', 'bleu', 'noir', 'clap', 'jump'],
        bg: ['синьо', 'жълто', 'зелено', 'червено', 'оранжево', 'синьо', 'черно', 'пляскане', 'скок'],
        es: ['azul', 'naranja', 'verde', 'rojo', 'negro', 'amarillo', 'aplaudir', 'saltar'],
    };

    function DEFAULT_DATA() {
        return {
            isMusicOn: false,
            isLoading: false,
            pictures: false,
            wordsMovement: false,
            showTime: 0,
            timeLimit: 0.5,
            loadedSound: 0,
            gameFinished: false,
            bgMusicInstance: null,
        };
    }

    export default {
        components: { ChameleonGameContainer },
        mixins: [playBoxGameMixin],
        data() {
            return DEFAULT_DATA();
        },
        watch: {
            time(val) {
                if (val >= this.timeLimit * 60) {
                    this.finishGame();
                }
            },
        },
        mounted() {
            this.startGame();
        },
        methods: {
            assignDataFromParameters() {
                if (!this.gameParameters) {
                    return this.$router.push({ name: 'dashboard' });
                }
                this.isMusicOn = this.gameParameters.isMusicOn;
                this.wordsMovement = this.gameParameters.wordsMovement;
                this.pictures = this.gameParameters.pictures;
                this.showTime = this.gameParameters.showTime;
                this.timeLimit = this.gameParameters.timeLimit;
            },
            startGame() {
                Object.assign(this, DEFAULT_DATA());
                this.assignDataFromParameters();

                this.$createjs.Sound.removeAllSounds();
                this.$createjs.Sound.removeAllEventListeners();
                this.$createjs.Sound.addEventListener('fileload', this.soundLoadTest);
                this.$createjs.Sound.alternateExtensions = ['mp3'];
                this.$createjs.Sound.registerSounds(this.chameleonSoundObjects);

                this.$nextTick(() => {
                    this.setTimer();
                });
            },
            soundLoadTest() {
                this.loadedSound++;
                if (this.loadedSound === this.chameleonSoundObjects.length && this.isMusicOn) {
                    this.bgMusicInstance = this.$createjs.Sound.play('music' + _.random(1, trainerConfig.music.length));
                    this.bgMusicInstance.on('complete', this.playRandomMusic, this);
                    this.bgMusicInstance.volume = 0.1;
                    this.isLoading = false;
                }
            },
            playRandomMusic() {
                this.bgMusicInstance.stop();
                this.bgMusicInstance = this.$createjs.Sound.play('music' + _.random(1, trainerConfig.music.length));
                this.bgMusicInstance.on('complete', this.playRandomMusic, this);
                this.bgMusicInstance.volume = 0.1;
            },
            finishGame() {
                if (this.isMusicOn) {
                    this.bgMusicInstance.stop();
                }
                this.stopTimer();
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.gameFinished = true;
                this.sendStat();
            },
            restartGame() {
                clearTimeout(this.counterTimeout);
                this.resetTimer();
                this.startGame();
            },
            refreshGameStatBeforeSend() {
                this.gameStat.parameters = _.cloneDeep(this.parameters);
                this.gameStat.answers = 0;
                this.gameStat.attempts = 1;
                this.gameStat.percent = 0;
                this.gameStat.isCompleted = true;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
            },
            fetchData() {
                this.getExercise();
            },
        },
        computed: {
            chameleonSoundObjects() {
                let sounds = [];
                sounds.push(...this.gamesConfig.common.sounds);
                sounds.push(...trainerConfig.music);
                return sounds;
            },
            ...mapState({
                gameParameters: state => state.trainer.game,
                user: state => state.auth.user,
            }),
            preparedItems() {
                return OBJECT_OF_POSSIBLE_ANSWERS;
            },
            showRestartButton() {
                return !this.exerciseId;
            },
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box--warning {
        display: table;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .playbox__container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__reaction {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
        width: 100%;
        flex-direction: column;
        @include stack-space($space-base);
        @include media($md) {
            flex-wrap: nowrap;
        }
        @media screen and (max-width: 655px) {
            justify-content: space-between;
        }
    }

    .play-box__reaction-side {
        align-self: normal;
        font-weight: 500;
        font-size: 20px;
        margin-bottom: 10px;
        @include media($sm) {
            width: auto;
            font-size: 28px;
        }
        @include media($md) {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: center;
            font-size: 20px;
        }
        @media screen and ($md) and (max-width: 800px) {
            font-size: 18px;
        }
        @include media($lg) {
            margin-bottom: 15px;
            font-size: 30px;
        }
        color: $hsl-color-green-58;
    }

    .result_buttons {
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .play-box__reaction-side--correct {
        color: $hsl-color-green-58;
    }
</style>
